import {useErrorLogs, useSuccessLogs} from '@api'
import {getUserData, renderClickableCell, useViewNavigate} from '@common'
import {NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef} from '@mui/x-data-grid'
import {pageLinksAtom, getUserTimeFormatAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {formatDate} from 'date-fns'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

import {ComposedComponents} from '@components'

export enum LogsTab {
  ACTIVITY,
  ERROR,
}

export interface TableData {
  time: string
  message: string
  id?: string
}

export function useLogsSearchModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const {t} = useTranslation()

  const currentUser = getUserData()
  const getUserTimeFormat = useAtomValue(getUserTimeFormatAtom)
  const userTimeFormat = getUserTimeFormat(currentUser.id)
  const [activeTab, setActiveTab] = useState<LogsTab>(LogsTab.ACTIVITY)

  const {data: errorLogs} = useErrorLogs()
  const {data: successLogs} = useSuccessLogs()

  const activityRows = useMemo(() => {
    if (!successLogs) return []
    else
      return successLogs
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map(item => ({
          message: item.message.replace(item.contractId, ''),
          time: formatDate(item.createdAt, userTimeFormat),
          id: item.contractId,
        }))
        .slice(0, 10)
  }, [successLogs])

  const errorRows = useMemo(() => {
    if (!errorLogs) return []
    else
      return errorLogs
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map(item => ({
          message: item.errorStep,
          time: formatDate(item.createdAt, userTimeFormat),
        }))
        .slice(0, 10)
  }, [errorLogs])

  useEffect(
    () =>
      setTitleLinks([
        {
          title: t(TranslationKey['Admin panel']),
          link: NavigationPathKey.ADMIN_PANEL,
        },
        {
          title: t(TranslationKey['Logs search']),
        },
      ]),
    [],
  )
  const navigate = useViewNavigate()

  /**
   * Error table columns configuration
   */
  const errorColumns: GridColDef[] = [
    {field: 'message', headerName: t(TranslationKey.Description), flex: 1},
    {field: 'time', headerName: t(TranslationKey.Timestamp), flex: 0.5, align: 'right', headerAlign: 'right'},
  ]

  /**
   * Activity table columns configuration
   */
  const activityColumns: GridColDef[] = [
    {
      field: 'message',
      headerName: t(TranslationKey.Description),
      flex: 1,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.CONTRACT_VIEW}/${params.row.id}`)),
    },
    {field: 'time', headerName: t(TranslationKey.Timestamp), flex: 0.5, align: 'right', headerAlign: 'right'},
  ]

  /**
   * Tab buttons configuration
   */
  const tabButtons: ComposedComponents.TabButtonProps<LogsTab>[] = [
    {
      label: t(TranslationKey['Activity logs']),
      key: LogsTab.ACTIVITY,
      onClick: () => setActiveTab(LogsTab.ACTIVITY),
    },
    {
      label: t(TranslationKey['Error logs']),
      key: LogsTab.ERROR,
      onClick: () => setActiveTab(LogsTab.ERROR),
    },
  ]

  return {activeTab, errorColumns, activityColumns, errorRows, activityRows, tabButtons, t}
}
